/* 5MC2 Cloudsteppers: seamless, manually scrollable horizontal loops.
 * Duplicate groups exist only to make the sequence continuous; originals keep keyboard focus.
 * No autoplay and no third-party carousel libraries.
 */
(() => {
  if (window.CSInfiniteCarousel) return;

  function makeAccessibleClone(group) {
    const copy = group.cloneNode(true);
    copy.setAttribute('aria-hidden', 'true');
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      if (element.id) element.removeAttribute('id');
      for (const attr of Array.from(element.attributes)) {
        if (attr.name.startsWith('data-shopify-')) element.removeAttribute(attr.name);
      }
      if (element.matches('a, button, input, select, textarea, [tabindex]')) {
        element.setAttribute('tabindex', '-1');
      }
    }
    return copy;
  }

  const instances = new WeakMap();

  function init(track) {
    if (!track || track.dataset.csLoopInit === 'yes') return;
    const existing = instances.get(track);
    if (existing) existing.destroy();
    const group = track.querySelector('[data-cs-loop-group]');
    if (!group || !group.children.length) return;

    track.dataset.csLoopInit = 'yes';
    let busy = false;
    let period = 0;
    let copies = 0;
    let center = 0;
    let lastWidth = 0;
    let observer = null;
    let resizeFrame = null;

    function build() {
      const original = track.querySelector('[data-cs-loop-original="yes"]') || group;
      original.dataset.csLoopOriginal = 'yes';
      track.replaceChildren(original);
      track.scrollLeft = 0;
      const width = original.getBoundingClientRect().width;
      if (!width) return;

      period = width;
      lastWidth = track.clientWidth;
      copies = Math.max(5, Math.ceil(lastWidth / period) + 5);
      if (copies % 2 === 0) copies++;
      center = Math.floor(copies / 2);

      const fragment = document.createDocumentFragment();
      for (let index = 0; index < copies; index++) {
        fragment.appendChild(index === center ? original : makeAccessibleClone(original));
      }
      track.replaceChildren(fragment);
      track.scrollLeft = period * center;
    }

    function loop() {
      if (busy || !period) return;
      const x = track.scrollLeft;
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (x < period || x > max - period) {
        busy = true;
        const middle = period * center;
        const wrapped = ((x - middle) % period + period) % period;
        track.scrollLeft = middle + wrapped;
        busy = false;
      }
    }

    build();
    track.addEventListener('scroll', loop, { passive: true });
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        const width = track.clientWidth;
        if (Math.abs(width - lastWidth) < 3) return;
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(build);
      });
      observer.observe(track);
    }
    instances.set(track, {
      destroy() {
        track.removeEventListener('scroll', loop);
        if (observer) observer.disconnect();
        if (resizeFrame) cancelAnimationFrame(resizeFrame);
      }
    });
  }

  function scroll(track, direction) {
    if (!track) return;
    const first = track.querySelector('[data-cs-loop-group] > *');
    if (!first) return;
    const group = first.parentElement;
    const style = getComputedStyle(group);
    const gap = parseFloat(style.columnGap || style.gap) || 0;
    const distance = first.getBoundingClientRect().width + gap;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    track.scrollBy({ left: direction * distance, behavior: reduced ? 'auto' : 'smooth' });
  }

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-cs-loop-dir]');
    if (!arrow) return;
    const section = arrow.closest('[data-cs-loop-section]');
    if (!section) return;
    scroll(section.querySelector('[data-cs-infinite-track]'), Number(arrow.dataset.csLoopDir));
  });

  window.CSInfiniteCarousel = { init, scroll };
  const initAll = (root = document) => root.querySelectorAll('[data-cs-infinite-track]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
